import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

export default function RefundPolicy() {
  const locale = sessionStorage.getItem('versant_locale') || 'en';

  const translations = {
    en: {
      title: "Refund & Cancellation Policy",
      date: "Effective Date: January 1, 2026",
      h1: "1. Overview",
      p1: "At VersantPro, we strive to deliver high-quality test preparation resources, AI evaluation tools, and study materials. Because our platform offers digital subscriptions, online software features, and downloadable digital assets, refund eligibility depends on the specific product type purchased as detailed below.",
      h2: "2. Digital eBook Purchases (Strictly Non-Refundable)",
      p2: "All purchases of the Versant Test Mastery eBook and associated downloadable digital resources are 100% final, non-returnable, and non-refundable.",
      c2: "Rationale: The eBook is an intangible, downloadable digital product. Once access or download links are delivered upon successful payment, the digital asset cannot be physically returned or revoked. By completing an eBook purchase, you explicitly acknowledge and agree that no refunds or exchanges will be granted.",
      h3: "3. Subscriptions & Pass Plans Refund Rules",
      p3: "Refund eligibility depends on the tier of pass purchased:",
      b3_0: "45-Day Pass (₹699 / $6.99) & Section Packs: Time-limited digital passes (including the 45-Day Sprint Pass and individual Section Packs) provide instant full access to AI systems and practice drills and are strictly non-refundable once purchased.",
      b3_1: "Lifetime VIP Pass (₹1,449 / $14.99) Eligibility: We offer a 7-day money-back guarantee exclusively on initial Lifetime VIP Pass purchases. If you are unsatisfied, you may request a full refund within 7 calendar days of purchase.",
      b3_2: "Usage Limits for VIP Pass: To prevent abuse, refunds for the Lifetime VIP Pass will only be approved if the candidate has attempted fewer than 3 mock tests during the 7-day period.",
      b3_3: "After 7 Days: Refund requests submitted after 7 calendar days from the purchase date will be automatically declined.",
      h4: "4. Technical Exceptions & Duplicate Charges",
      p4: "Notwithstanding the above, a full refund will be processed for any product type under the following technical circumstances:",
      b4_1: "Duplicate Transaction: You were accidentally charged twice for the exact same transaction due to a gateway or system glitch.",
      b4_2: "Total Non-Delivery: A system error on our platform actively prevented access to your account and our support team was unable to resolve the issue within 48 hours.",
      h5: "5. How to Request a Refund",
      p5: "To initiate a eligible Lifetime VIP Pass refund within the 7-day window or report a duplicate transaction, please contact our support team:",
      c5_1: "Support Email: hello@versantpro.com",
      c5_2: "Required Details: Registered Email, Order ID / Payment Reference ID, and Reason for Request.",
      c5_3: "Approved refunds are processed back to the original payment method within 5–7 business days."
    },
    es: {
      title: "Política de Reembolso y Cancelación",
      date: "Fecha de vigencia: 1 de enero de 2026",
      h1: "1. Descripción General",
      p1: "En VersantPro, nos esforzamos por ofrecer recursos de preparación de exámenes, herramientas de evaluación de IA y materiales de estudio de alta calidad. Debido a que nuestra plataforma ofrece suscripciones digitales y activos digitales descargables, la elegibilidad de reembolso depende del tipo de producto específico adquirido.",
      h2: "2. Compras de eBook Digital (Estrictamente No Reembolsable)",
      p2: "Todas las compras del eBook Versant Test Mastery y los recursos digitales descargables asociados son 100% finales y no reembolsables.",
      c2: "Justificación: El eBook es un producto digital descargable intangible. Una vez entregados los enlaces de descarga tras un pago exitoso, el activo digital no se puede devolver. Al completar la compra, aceptas explícitamente que no se otorgarán reembolsos.",
      h3: "3. Reglas de Reembolso para Planes y Pases",
      p3: "La elegibilidad para reembolso depende del nivel de pase adquirido:",
      b3_0: "Pase de 45 Días (699 INR / $6.99) y Paquetes de Sección: Los pases digitales de tiempo limitado (incluido el Pase de 45 Días y los paquetes de sección) son estrictamente no reembolsables una vez adquiridos.",
      b3_1: "Elegibilidad del Pase VIP De Por Vida (1,449 INR / $14.99): Ofrecemos una garantía de reembolso de 7 días exclusivamente para el Pase VIP De Por Vida. Puedes solicitar un reembolso completo dentro de los 7 días naturales posteriores a tu compra.",
      b3_2: "Límites de uso: Para evitar abusos, los reembolsos solo se aprobarán si el candidato ha intentado menos de 3 simulacros de examen durante el período de 7 días.",
      b3_3: "Después de 7 días: Las solicitudes enviadas después de 7 días naturales se rechazarán automáticamente.",
      h4: "4. Excepciones Técnicas y Cargos Duplicados",
      p4: "Sin perjuicio de lo anterior, se procesará un reembolso completo bajo las siguientes circunstancias técnicas:",
      b4_1: "Transacción duplicada: Se te cobró dos veces por error debido a un problema de la pasarela de pago o del sistema.",
      b4_2: "Falta de entrega total: Un error del sistema impidió el acceso a tu cuenta y nuestro equipo no pudo resolverlo en 48 horas.",
      h5: "5. Cómo Solicitar un Reembolso",
      p5: "Para iniciar un reembolso dentro del plazo de 7 días, ponte en contacto con nuestro equipo de soporte:",
      c5_1: "Correo de soporte: hello@versantpro.com",
      c5_2: "Detalles requeridos: Correo electrónico registrado, ID de pedido / Referencia de pago y motivo de la solicitud.",
      c5_3: "Los reembolsos aprobados se devuelven al método de pago original en 5 a 7 días hábiles."
    },
    pt: {
      title: "Política de Reembolso e Cancelamento",
      date: "Data de Vigência: 1 de janeiro de 2026",
      h1: "1. Visão Geral",
      p1: "Na VersantPro, nos esforçamos para fornecer recursos de preparação para testes de alta qualidade, ferramentas de avaliação de IA e materiais de estudo. Como nossa plataforma oferece assinaturas digitais e arquivos digitais para download, a elegibilidade de reembolso depende do tipo de produto adquirido.",
      h2: "2. Compras de eBooks Digitais (Estritamente Não Reembolsáveis)",
      p2: "Todas as compras do eBook Versant Test Mastery e recursos digitais para download associados são 100% finais e não reembolsáveis.",
      c2: "Justificativa: O eBook é um produto digital para download. Assim que os links de acesso forem entregues após o pagamento, o produto digital não poderá ser devolvido. Ao concluir a compra, você concorda explicitamente que nenhum reembolso será concedido.",
      h3: "3. Regras de Reembolso para Planos e Passes",
      p3: "A elegibilidade de reembolso depende da categoria do passe adquirido:",
      b3_0: "Passe de 45 Dias (699 INR / $6.99) e Pacotes de Seção: Os passes digitais por tempo limitado são estritamente não reembolsáveis após a compra.",
      b3_1: "Elegibilidade do Passe VIP Vitalício (1.449 INR / $14.99): Oferecemos uma garantia de reembolso de 7 dias exclusivamente no Passe VIP Vitalício. Você pode solicitar o reembolso em até 7 dias corridos.",
      b3_2: "Limites de uso: Para evitar abusos, os reembolsos só serão aprovados se o candidato tiver realizado menos de 3 simulados durante o período de 7 dias.",
      b3_3: "Após 7 dias: As solicitações enviadas após 7 dias corridos serão automaticamente rejeitadas.",
      h4: "4. Exceções Técnicas e Cobranças Duplicadas",
      p4: "Apesar do exposto acima, um reembolso total será processado sob as seguintes circunstâncias técnicas:",
      b4_1: "Transação duplicada: Você foi cobrado duas vezes pela mesma transação devido a uma falha no gateway de pagamento.",
      b4_2: "Não entrega total: Um erro de sistema impediu o acesso à sua conta e nossa equipe não conseguiu resolver em até 48 horas.",
      h5: "5. Como Solicitar um Reembolso",
      p5: "Para iniciar um reembolso dentro do prazo de 7 dias ou relatar cobrança duplicada, entre em contato:",
      c5_1: "E-mail de suporte: hello@versantpro.com",
      c5_2: "Detalhes necessários: E-mail registrado, ID do pedido / Referência de pagamento e motivo da solicitação.",
      c5_3: "Os reembolsos aprovados são processados de volta ao método original em 5 a 7 dias úteis."
    },
    ja: {
      title: "返金・キャンセルポリシー",
      date: "効力発生日：2026年1月1日",
      h1: "1. 概要",
      p1: "VersantProでは、高品質なテスト対策リソース、AI評価ツール、および学習教材をお届けするよう努めております。当プラットフォームではデジタル購読（サブスクリプション）、オンラインソフトウェア機能、およびダウンロード可能なデジタルコンテンツを提供しているため、返金の可否は以下に示す購入製品の種類によって異なります。",
      h2: "2. 電子書籍（ダウンロード製品）の購入（返金不可）",
      p2: "「Versantテスト完全攻略電子書籍」および関連するダウンロード用デジタルリソースの購入は、すべて購入確定後のキャンセル・返品・返金は一切お受けできません。",
      c2: "理由：電子書籍は形のないダウンロード可能なデジタルコンテンツです。お支払いの完了と同時にアクセス用リンクまたはダウンロード用リンクが提供されるため、製品の物理的な返品やアクセス権の無効化を行うことが困難です。電子書籍をご購入いただいた時点で、返金や交換は行われないことに明示的に同意したものとみなされます。",
      h3: "3. 各プラン・パスの返金規定",
      p3: "返金の対象の可否はご購入いただいたパスの種類により異なります：",
      b3_0: "45日間スプリントパス（₹699 / $6.99）およびセクションパック：期間限定デジタルパス（45日パスおよびセクション別個別パック含む）は、購入後の返金は一切できません。",
      b3_1: "Lifetime VIP Pass（₹1,449 / $14.99）の返金条件：7日間の返金保証は「Lifetime VIP Pass」の新規購入に対してのみ適用されます。サービスにご満足いただけない場合、初回購入日から7暦日以内であれば全額返金を申請することができます。",
      b3_2: "VIPパスの利用制限：悪用を防ぐため、返金は該当の7日間に受験した模擬試験が3回未満である場合にのみ承認されます。",
      b3_3: "7日経過後：初回購入日から7暦日を過ぎた後の返金申請は、理由を問わず自動的に却下されます。",
      h4: "4. 技術的例外および二重請求",
      p4: "上記にかかわらず、以下の技術的な問題が発生した場合は、すべての製品において全額返金いたします。",
      b4_1: "二重決済：決済システムの不具合により、同一の取引に対して誤って二重に請求が行われた場合。",
      b4_2: "サービスの不達：システムの重大なエラーによりアカウントにアクセスできなくなり、当サイトのサポートチームが48時間以内に問題を解決できなかった場合。",
      h5: "5. 返金申請方法",
      p5: "7日以内の返金申請、または二重請求の報告を行う場合は、以下のサポート窓口までご連絡ください。",
      c5_1: "サポートメールアドレス：hello@versantpro.com",
      c5_2: "必要情報：ご登録メールアドレス、注文ID（Order ID）または決済の参照ID、および返金理由。",
      c5_3: "承認された返金は、5〜7営業日以内に元の決済手段に払い戻されます。"
    }
  };

  const t = translations[locale] || translations.en;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans flex flex-col">
      <Helmet>
        <title>{t.title} | VersantPro</title>
      </Helmet>
      <Header />
      <main className="flex-grow container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-8 tracking-tight">{t.title}</h1>
          
          <div className="prose prose-lg prose-emerald max-w-none text-gray-600 space-y-6">
            <p className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-xl inline-block">
              {t.date}
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2">{t.h1}</h2>
            <p>{t.p1}</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2">{t.h2}</h2>
            <p>{t.p2}</p>
            <p className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl text-amber-950 font-medium text-sm">
              {t.c2}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2">{t.h3}</h2>
            <p>{t.p3}</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              {t.b3_0 && <li className="font-semibold text-gray-800">{t.b3_0}</li>}
              <li>{t.b3_1}</li>
              <li>{t.b3_2}</li>
              <li>{t.b3_3}</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2">{t.h4}</h2>
            <p>{t.p4}</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>{t.b4_1}</li>
              <li>{t.b4_2}</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2">{t.h5}</h2>
            <p>{t.p5}</p>
            <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100 text-emerald-950 font-medium space-y-2">
              <p>{t.c5_1}</p>
              <p>{t.c5_2}</p>
              <p className="text-xs text-emerald-800">{t.c5_3}</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
